import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  BarcodeOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FormOutlined,
  KeyOutlined,
  NumberOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { CREATE_jwel1 } from "../../Config/Config";
import {
  AMBER,
  EMERALD,
  INDIGO,
  PURPLE,
  ROSE,
  ROW_COLORS,
  SKY,
  TEAL,
  getStyles,
  rowStyles,
} from "./Mastertheme";
import { fetchCodeConfigure, updateCodeConfigure } from "./Masterapi";

export default function CodeMaster() {
  const from = INDIGO;
  const to = PURPLE;
  const G = getStyles(from, to);

  const [form] = Form.useForm();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [filter, setFilter] = useState("");
  const [testNumber, setTestNumber] = useState("1500");

  const chrInputRef = useRef(null);

  /* ── GET: Fetch Code Master Data from API ───────────────────── */
  const fetchData = async () => {
    setLoading(true);
    try {
      // Use helper or direct call with fallback
      let data = [];
      try {
        data = await fetchCodeConfigure();
      } catch (innerErr) {
        // Direct fallback call with tenantName header
        const tenantName =
          localStorage.getItem("tenantName") ||
          sessionStorage.getItem("tenantName") ||
          "";
        const url = `${CREATE_jwel1 || "https://tssnewservices.timeserasoftware.in"}/api/Master/GetDataFromGivenTableName`;
        const res = await axios.get(url, {
          params: { tableName: "code_configure" },
          headers: { tenantName },
        });
        data = Array.isArray(res.data) ? res.data : [];
      }

      // Dynamically map API response (case-insensitive keys for NO and CHR)
      const formatted = (Array.isArray(data) ? data : []).map((item, idx) => {
        const noVal =
          item.NO !== undefined
            ? item.NO
            : item.no !== undefined
            ? item.no
            : item.No !== undefined
            ? item.No
            : idx;

        const chrVal =
          item.CHR !== undefined
            ? item.CHR
            : item.chr !== undefined
            ? item.chr
            : item.Chr !== undefined
            ? item.Chr
            : "";

        return {
          key: `code_${noVal !== undefined ? noVal : idx}`,
          NO: noVal,
          CHR: chrVal != null ? String(chrVal).trim() : "",
          raw: item,
        };
      });

      // Sort by NO numerically if possible
      formatted.sort((a, b) => Number(a.NO) - Number(b.NO));
      setTableData(formatted);
    } catch (err) {
      console.error("Error fetching Code Master data:", err);
      message.error("Failed to load Code Master data from server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ── Filter / Derived ──────────────────────────────────────── */
  const q = filter.trim().toLowerCase();
  const filteredData = q
    ? tableData.filter(
        (r) =>
          String(r.NO ?? "").toLowerCase().includes(q) ||
          String(r.CHR ?? "").toLowerCase().includes(q)
      )
    : tableData;

  const editingRecord =
    editingKey != null ? tableData.find((r) => r.key === editingKey) : null;

  /* ── Handlers ──────────────────────────────────────────────── */
  const handleEdit = (record) => {
    setEditingKey(record.key);
    form.setFieldsValue({
      NO: record.NO,
      CHR: record.CHR,
    });
    setTimeout(() => chrInputRef.current?.focus?.(), 100);
  };

  const handleCancel = () => {
    form.resetFields();
    setEditingKey(null);
  };

  const handleRefresh = () => {
    handleCancel();
    setFilter("");
    fetchData();
  };

  /* ── POST: Update Code Master Data ─────────────────────────── */
  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const escapeSql = (v) => String(v ?? "").replace(/'/g, "''");

      // Identify original record by NO
      const whereNo =
        editingRecord?.NO !== undefined
          ? editingRecord.NO
          : values.NO !== undefined
          ? values.NO
          : 0;

      const cleanChr = escapeSql(values.CHR || "").trim().toUpperCase();

      // Construct whereString and updateString according to specifications
      const whereString = `NO=${whereNo}`;
      let updateString = `CHR='${cleanChr}'`;

      // If NO was also updated to a different value
      if (
        values.NO !== undefined &&
        values.NO !== "" &&
        editingRecord &&
        Number(values.NO) !== Number(whereNo)
      ) {
        updateString += `, NO=${Number(values.NO)}`;
      }

      // Call update API
      try {
        await updateCodeConfigure({
          updateString,
          whereString,
        });
      } catch (apiErr) {
        // Fallback direct axios call
        const tenantName =
          localStorage.getItem("tenantName") ||
          sessionStorage.getItem("tenantName") ||
          "";
        const url = `${CREATE_jwel1 || "https://tssnewservices.timeserasoftware.in"}/api/Master/CommonUpdateWithWhere`;
        await axios.post(
          url,
          {
            tableName: "code_configure",
            updateString,
            whereString,
          },
          {
            headers: {
              "Content-Type": "application/json",
              tenantName,
            },
          }
        );
      }

      message.success(
        `Code mapping for Digit ${whereNo} updated to "${cleanChr}" successfully`
      );
      handleCancel();
      fetchData();
    } catch (err) {
      console.error("Update failed:", err);
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update code configuration"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── Live Cost Code Mapping Simulation ─────────────────────── */
  const codeMap = useMemo(() => {
    const map = {};
    tableData.forEach((r) => {
      if (r.NO !== undefined && r.NO !== null) {
        map[String(r.NO)] = r.CHR || "";
      }
    });
    return map;
  }, [tableData]);

  const simulatedCode = useMemo(() => {
    if (!testNumber) return "";
    return String(testNumber)
      .split("")
      .map((ch) => (codeMap[ch] !== undefined ? codeMap[ch] : ch))
      .join("");
  }, [testNumber, codeMap]);

  /* ── Columns ───────────────────────────────────────────────── */
  const columns = [
    {
      title: "#",
      width: 58,
      align: "center",
      render: (_, __, idx) => {
        const color = ROW_COLORS[idx % ROW_COLORS.length];
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `${color}18`,
              color,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {idx + 1}
          </span>
        );
      },
    },
    {
      title: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            color: from,
          }}
        >
          <NumberOutlined /> Number / Digit (NO)
        </span>
      ),
      dataIndex: "NO",
      width: 170,
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag
            style={{
              background: "#eef2ff",
              color: INDIGO,
              border: "1px solid #c7d2fe",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 13,
              padding: "2px 10px",
            }}
          >
            Digit {v}
          </Tag>
        </div>
      ),
    },
    {
      title: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            color: PURPLE,
          }}
        >
          <KeyOutlined /> Code Character (CHR)
        </span>
      ),
      dataIndex: "CHR",
      render: (v) =>
        v ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 44,
              padding: "3px 14px",
              borderRadius: 8,
              background: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
              border: "1px solid #f0abfc",
              color: "#9333ea",
              fontWeight: 800,
              fontSize: 15,
              letterSpacing: "0.5px",
              boxShadow: "0 2px 6px rgba(147, 51, 234, 0.08)",
            }}
          >
            {v}
          </span>
        ) : (
          <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
        ),
    },
    {
      title: "Mapping Preview",
      width: 160,
      render: (_, r) => (
        <span style={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>
          {r.NO} <span style={{ color: PURPLE }}>➜</span>{" "}
          <strong style={{ color: "#1e293b" }}>{r.CHR || "—"}</strong>
        </span>
      ),
    },
    {
      title: "Actions",
      width: 90,
      align: "center",
      render: (_, record) => (
        <div
          style={{ display: "flex", gap: 6, justifyContent: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit Code Mapping">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{
                background: `${INDIGO}15`,
                color: INDIGO,
                border: `1px solid ${INDIGO}33`,
                borderRadius: 8,
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div style={G.page}>
      {/* Header Bar */}
      <div style={G.headerBar}>
        <div style={G.headerLeft}>
          <div style={G.headerIcon}>
            <BarcodeOutlined />
          </div>
          <div>
            <h1 style={G.headerTitle}>Code Master</h1>
            <p style={G.headerSub}>
              Configure secret cost code character mappings (0–9) for barcode price tags
            </p>
          </div>
        </div>

        <div style={G.statsRow}>
          <div style={G.stat(from)}>
            <span style={G.statNum(from)}>{tableData.length}</span>
            <span style={G.statLabel}>Total Digits</span>
          </div>
          <div style={G.stat(EMERALD)}>
            <span style={G.statNum(EMERALD)}>
              {tableData.filter((r) => r.CHR && r.CHR !== "-").length}
            </span>
            <span style={G.statLabel}>Mapped</span>
          </div>
          <div style={G.stat(SKY)}>
            <span style={G.statNum(SKY)}>{filteredData.length}</span>
            <span style={G.statLabel}>Filtered</span>
          </div>
        </div>
      </div>

      <Row gutter={[20, 20]} align="top">
        {/* Table Panel */}
        <Col xs={24} xl={15}>
          <div style={G.card}>
            <div style={G.panelHead(from, to)}>
              <span style={G.panelTitle}>
                <UnorderedListOutlined style={{ fontSize: 16 }} />
                Configured Code Mappings
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 20,
                  padding: "3px 12px 3px 8px",
                  gap: 6,
                  minWidth: 200,
                }}
              >
                <SearchOutlined
                  style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}
                />
                <input
                  className="mc-search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search number or code…"
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#fff",
                    fontSize: 13,
                    width: "100%",
                  }}
                />
                {filter && (
                  <CloseCircleOutlined
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                    onClick={() => setFilter("")}
                  />
                )}
              </div>
            </div>

            <div style={{ padding: "4px 0" }}>
              <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                size="middle"
                pagination={false}
                scroll={{ y: "calc(100vh - 330px)" }}
                rowClassName={(r) =>
                  r.key === editingKey ? "mc-row-selected" : "mc-row"
                }
                onRow={(record) => ({
                  onClick: () => handleEdit(record),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </div>
        </Col>

        {/* Form & Simulator Panel */}
        <Col xs={24} xl={9}>
          <div style={G.card}>
            <div style={G.panelHead(PURPLE, INDIGO)}>
              <span style={G.panelTitle}>
                <FormOutlined style={{ fontSize: 16 }} />
                {editingKey != null
                  ? `Edit Mapping: Digit ${editingRecord?.NO}`
                  : "Code Master Details"}
              </span>
              {editingKey != null && (
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    border: "none",
                    color: "#fff",
                    borderRadius: 8,
                    width: 28,
                    height: 28,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                  }}
                  title="Cancel editing"
                >
                  <CloseCircleOutlined />
                </button>
              )}
            </div>

            <div style={G.panelBody}>
              {editingKey == null ? (
                <div style={G.hint}>
                  <BarcodeOutlined style={{ color: from, fontSize: 16 }} />
                  <span>
                    Click any row in the table to modify its character mapping.
                  </span>
                </div>
              ) : (
                editingRecord && (
                  <div style={G.selectedBadge(PURPLE)}>
                    <div style={G.badgeAvatar(PURPLE)}>
                      <KeyOutlined />
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "#1e293b",
                          fontSize: 13,
                        }}
                      >
                        Editing Digit {editingRecord.NO}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        Current character:{" "}
                        <strong style={{ color: PURPLE }}>
                          "{editingRecord.CHR || "None"}"
                        </strong>
                      </div>
                    </div>
                  </div>
                )
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
              >
                <div style={G.sectionLabel(INDIGO)}>
                  <span style={G.dot(INDIGO)} />
                  Mapping Information
                </div>

                {/* Field: NO */}
                <Form.Item
                  label={
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: 13,
                      }}
                    >
                      Number / Digit (NO)
                    </span>
                  }
                  name="NO"
                  rules={[
                    { required: true, message: "Please enter the number" },
                  ]}
                >
                  <Input
                    type="number"
                    prefix={
                      <NumberOutlined
                        style={{ color: INDIGO, fontSize: 15 }}
                      />
                    }
                    placeholder="e.g. 0"
                    style={{ ...G.inputStyle, background: "#f8fafc" }}
                    disabled={editingKey != null}
                  />
                </Form.Item>

                {/* Field: CHR */}
                <Form.Item
                  label={
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: 13,
                      }}
                    >
                      Code Character (CHR) <span style={{ color: ROSE }}>*</span>
                    </span>
                  }
                  name="CHR"
                  rules={[
                    { required: true, message: "Please enter the code character" },
                  ]}
                >
                  <Input
                    ref={chrInputRef}
                    maxLength={10}
                    placeholder="e.g. M"
                    prefix={<KeyOutlined style={{ color: PURPLE, fontSize: 15 }} />}
                    style={G.inputStyle}
                    onChange={(e) => {
                      const upper = (e.target.value || "").toUpperCase();
                      form.setFieldsValue({ CHR: upper });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        form.submit();
                      }
                    }}
                  />
                </Form.Item>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    disabled={editingKey == null}
                    icon={<SaveOutlined />}
                    style={{
                      ...G.primaryBtn,
                      opacity: editingKey == null ? 0.6 : 1,
                      cursor: editingKey == null ? "not-allowed" : "pointer",
                    }}
                  >
                    {editingKey != null
                      ? `Update Digit ${editingRecord?.NO ?? ""}`
                      : "Select Row to Update"}
                  </Button>

                  {editingKey != null && (
                    <Button
                      onClick={handleCancel}
                      icon={<CloseCircleOutlined />}
                      style={{
                        borderRadius: 10,
                        height: 42,
                        fontWeight: 600,
                        border: `1px solid ${ROSE}44`,
                        color: ROSE,
                        background: `${ROSE}08`,
                      }}
                    >
                      Cancel
                    </Button>
                  )}

                  <Tooltip title="Refresh from server">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleRefresh}
                      loading={loading}
                      style={{
                        borderRadius: 10,
                        height: 42,
                        border: `1px solid ${from}44`,
                        color: from,
                        background: `${from}08`,
                      }}
                    />
                  </Tooltip>
                </div>
              </Form>
            </div>
          </div>

          {/* Live Cost Code Simulator */}
          <div
            style={{
              marginTop: 16,
              background: "#fff",
              borderRadius: 14,
              padding: "16px 20px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              border: "1px solid #e0e7ff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 700,
                color: INDIGO,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                marginBottom: 10,
              }}
            >
              <ThunderboltOutlined style={{ color: AMBER, fontSize: 15 }} />
              Live Cost Code Simulator
            </div>

            <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 10px 0" }}>
              Enter any purchase amount or cost to test the active character encryption:
            </p>

            <Input
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 1500"
              prefix={<span style={{ color: "#94a3b8", fontWeight: 700 }}>₹</span>}
              style={{ height: 38, borderRadius: 8, marginBottom: 12 }}
            />

            <div
              style={{
                background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
                borderRadius: 10,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: "#a5b4fc", textTransform: "uppercase" }}>
                  Barcode Tag Secret Code
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 900,
                    color: "#fbbf24",
                    letterSpacing: "4px",
                    fontFamily: "monospace",
                    marginTop: 2,
                  }}
                >
                  {simulatedCode || "—"}
                </div>
              </div>
              <Tag
                color="gold"
                style={{
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: 11,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <CheckCircleOutlined /> Active
              </Tag>
            </div>
          </div>

          {/* Quick Tips */}
          <div
            style={{
              marginTop: 16,
              background: "#fff",
              borderRadius: 14,
              padding: "14px 18px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.7,
                marginBottom: 10,
              }}
            >
              Quick Tips
            </div>
            {[
              {
                color: from,
                text: "Click any digit in the table to update its code character",
              },
              {
                color: PURPLE,
                text: "Code characters are automatically converted to uppercase",
              },
              {
                color: AMBER,
                text: "Used across barcode generation to encrypt cost prices on tags",
              },
              {
                color: EMERALD,
                text: "Changes are saved directly to the database via CommonUpdateWithWhere",
              },
            ].map((tip, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  marginBottom: 7,
                  fontSize: 12,
                  color: "#475569",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: tip.color,
                    flexShrink: 0,
                    marginTop: 4,
                  }}
                />
                {tip.text}
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <style>{rowStyles(from)}</style>
    </div>
  );
}
